import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';

export const permissionGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const requiredPermissions = route.data?.['permissions'] as string[] | undefined;

  if (!authService.isAuthenticated()) {
    router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }

  if (!requiredPermissions?.length) {
    return true;
  }

  const currentUser = await getCurrentUser(authService);
  if (hasAnyPermission(requiredPermissions, currentUser)) {
    return true;
  }

  router.navigate(['/']);
  return false;
};

async function getCurrentUser(authService: AuthService): Promise<any> {
  const currentUser = await firstValueFrom(authService.currentUser$);
  if (currentUser) {
    return currentUser;
  }

  try {
    return await firstValueFrom(authService.getCurrentUser());
  } catch {
    return null;
  }
}

function hasAnyPermission(requiredPermissions: string[], currentUser: any): boolean {
  const userPermissionSources = [
    currentUser?.permissions,
    currentUser?.global_permissions,
    getStoredArray('permissions')
  ];

  return requiredPermissions.some((requiredPermission) =>
    userPermissionSources.some((source) => hasPermissionInSource(source, requiredPermission))
  );
}

function getStoredArray(key: string): unknown[] {
  const value = localStorage.getItem(key);
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function hasPermissionInSource(source: unknown, requiredPermission: string): boolean {
  if (!Array.isArray(source)) {
    return false;
  }

  return source.some((permission: any) => {
    if (typeof permission === 'string') {
      return permission === requiredPermission;
    }

    const permissionValue = permission?.permission || permission?.name;
    return permissionValue === requiredPermission;
  });
}
